import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, X, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import PanoramaViewer from "@/components/PanoramaViewer";
import { api, formatApiError } from "@/lib/api";
import { BRAND, IMAGES } from "@/lib/data";

const PROPERTY = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5 BHK", "Villa / Duplex", "Office", "Restaurant / Café", "Retail Shop", "Salon / Clinic", "Other Commercial"];
const SCOPE = ["Full Home", "Modular Kitchen", "Wardrobes", "Kitchen + Wardrobes", "Turnkey Commercial", "Office Interior", "Restaurant / Café Interior", "Retail / Shop Interior"];
const BUDGET = ["Under ₹5L", "₹5L – ₹10L", "₹10L – ₹20L", "₹20L – ₹50L", "₹50L – ₹75L", "₹75L – ₹1Cr", "₹1Cr+"];

const initial = { name: "", email: "", phone: "", city: "", property_type: PROPERTY[0], scope: SCOPE[0], budget: BUDGET[0], message: "" };

const COMMERCIAL_PROPERTY_SET = new Set(["Office", "Restaurant / Café", "Retail Shop", "Salon / Clinic", "Other Commercial"]);

export default function Consultation() {
  const [searchParams] = useSearchParams();
  const intent = (searchParams.get("intent") || "").toLowerCase(); // "commercial" | ""
  const isCommercialIntent = intent === "commercial";
  const refCode = (searchParams.get("ref") || "").toUpperCase().trim();

  const [form, setForm] = useState(() => {
    const seed = { ...initial };
    if (isCommercialIntent) {
      seed.property_type = "Office";
      seed.scope = "Turnkey Commercial";
    }
    const seedEmail = searchParams.get("email");
    const seedName = searchParams.get("name");
    if (seedEmail) seed.email = seedEmail;
    if (seedName) seed.name = seedName;
    return seed;
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [design, setDesign] = useState(null);
  const [referrer, setReferrer] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem("zebo_ai_design");
    if (raw) {
      try { setDesign(JSON.parse(raw)); } catch { /* ignore */ }
    }
  }, []);

  // Validate referral code — only surface the banner if the code is real
  useEffect(() => {
    if (!refCode) return;
    api.get(`/refer/validate?code=${encodeURIComponent(refCode)}`)
      .then((r) => { if (r.data?.valid) setReferrer(r.data.referrer_name); })
      .catch(() => {});
  }, [refCode]);

  const removeDesign = () => {
    setDesign(null);
    localStorage.removeItem("zebo_ai_design");
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.city) {
      toast.error("Please fill in your name, email, phone and city.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/consultations", {
        ...form,
        design_image: design?.image || null,
        design_prompt: design?.prompt || null,
        panorama_image: design?.panorama || null,
        ref: referrer ? refCode : null,
      });
      localStorage.removeItem("zebo_ai_design");
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "consultation",
          scope: form.scope,
          property_type: form.property_type,
        });
      }
      setDone(true);
      toast.success("Request received! Our team will call you shortly.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full border border-[#143D63]/20 bg-[#F9F8F6] px-4 py-3 text-sm text-[#143D63] focus:border-[#F8C21C] focus:outline-none";
  const label = "overline text-[#143D63]/60 mb-2 block";

  const isCommercial = isCommercialIntent
    || COMMERCIAL_PROPERTY_SET.has(form.property_type)
    || (form.scope || "").toLowerCase().includes("commercial")
    || (form.scope || "").toLowerCase().includes("office")
    || (form.scope || "").toLowerCase().includes("restaurant")
    || (form.scope || "").toLowerCase().includes("retail");

  const heading = isCommercial
    ? { overline: "Free commercial consultation", title: <>Let&apos;s plan<br />your commercial space</>, sub: `Book a no-obligation site visit with ${BRAND.name}. Share a few details about your office, restaurant, café or retail project and our team will reach out within 24 hours.` }
    : { overline: "Free consultation", title: <>Let&apos;s plan<br />your dream home</>, sub: `Book a no-obligation design consultation with ${BRAND.name}. Share a few details and our design team will reach out within 24 hours.` };

  return (
    <main data-testid="consultation-page" className="pt-32 pb-28 relative z-10">
      <Seo
        title="Book a Free Interior Design Consultation in Kolkata — ZEBO"
        description="Book a free, no-obligation interior design consultation with ZEBO India Private Limited in Kolkata. Full-home interiors, modular kitchens and wardrobes."
        path="/consultation"
      />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-5">
          <p className="overline text-[#8B0000] mb-4">{heading.overline}</p>
          <h1 data-testid="consult-heading" className="font-serif text-6xl sm:text-7xl font-light tracking-tight text-[#143D63] leading-[0.95]">
            {heading.title}
          </h1>
          <p className="text-[#143D63]/70 mt-6 max-w-md">
            {heading.sub}
          </p>
          <div className="mt-10 border border-[#143D63]/15 overflow-hidden aspect-[4/3] hidden lg:block">
            <img src={IMAGES.kitchen1} alt="Interior" className="w-full h-full object-cover" />
          </div>
        </Reveal>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              data-testid="consultation-success"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="border border-[#143D63]/15 bg-white p-12 text-center h-full flex flex-col items-center justify-center"
            >
              <CheckCircle2 className="text-[#F8C21C] mb-6" size={64} />
              <h2 className="font-serif text-4xl text-[#143D63] mb-3">Thank you, {form.name.split(" ")[0]}!</h2>
              <p className="text-[#143D63]/70 max-w-md">
                Your consultation request has been received. Our design team will contact you at <span className="font-semibold text-[#143D63]">{form.phone}</span> within 24 hours.
              </p>

              {design && (design.panorama || design.image) && (
                <div data-testid="success-360" className="w-full max-w-lg mt-8">
                  <p className="overline text-[#8B0000] mb-3 flex items-center justify-center gap-2">
                    <Sparkles size={12} /> {design.panorama ? "Step inside your dream room — drag to look around" : "Your attached design"}
                  </p>
                  <div className="border border-[#143D63]/15 aspect-[4/3] overflow-hidden bg-[#143D63]">
                    {design.panorama ? (
                      <PanoramaViewer src={design.panorama} />
                    ) : (
                      <img src={design.image} alt="Your AI room design" className="w-full h-full object-cover" />
                    )}
                  </div>
                </div>
              )}

              <button
                data-testid="consultation-reset"
                onClick={() => { setForm(initial); setDone(false); }}
                className="mt-8 bg-[#143D63] text-[#F9F8F6] px-6 py-3 text-sm font-bold hover:bg-[#8B0000] transition-colors"
              >
                Submit another request
              </button>
              <a
                href={BRAND.reviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-review-link"
                className="mt-4 text-sm text-[#8B0000] font-medium link-underline"
              >
                Already a happy client? Review us on Google ★
              </a>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="consultation-form" className="border border-[#143D63]/15 bg-white p-7 sm:p-10 space-y-6">
              {referrer && (
                <div data-testid="referral-banner" className="bg-[#F8C21C]/15 border border-[#F8C21C] p-4 flex items-start gap-3">
                  <div className="text-2xl leading-none">🎁</div>
                  <div className="flex-1">
                    <p className="font-serif text-lg text-[#143D63] leading-tight">
                      {referrer} referred you — you'll get an extra design consultation on us.
                    </p>
                    <p className="text-xs text-[#143D63]/60 mt-1">Referral code <span className="font-bold text-[#8B0000]">{refCode}</span> applied automatically.</p>
                  </div>
                </div>
              )}
              {design && (
                <div data-testid="attached-design" className="flex gap-4 items-center border border-[#F8C21C] bg-[#FFF7DA] p-3">
                  <img src={design.image} alt="Attached AI design" className="w-20 h-20 object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="overline text-[#8B0000] mb-1 flex items-center gap-1"><Sparkles size={12} /> AI design attached</p>
                    <p className="text-sm text-[#143D63] font-medium truncate">{design.prompt}</p>
                    <p className="text-xs text-[#143D63]/60">Our designers will see this with your request.</p>
                  </div>
                  <button type="button" data-testid="remove-design-btn" onClick={removeDesign} className="text-[#143D63]/60 hover:text-[#8B0000] transition-colors shrink-0" aria-label="Remove attached design">
                    <X size={18} />
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={label}>Full name</label>
                  <input data-testid="consult-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className={label}>Phone</label>
                  <input data-testid="consult-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="+91…" />
                </div>
                <div>
                  <label className={label}>Email</label>
                  <input data-testid="consult-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div>
                  <label className={label}>City</label>
                  <input data-testid="consult-city" className={field} value={form.city} onChange={set("city")} placeholder="City" />
                </div>
              </div>

              <div className="grid sm:grid-cols-3 gap-6">
                <div>
                  <label className={label}>Property</label>
                  <select data-testid="consult-property" className={field} value={form.property_type} onChange={set("property_type")}>
                    {PROPERTY.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Scope</label>
                  <select data-testid="consult-scope" className={field} value={form.scope} onChange={set("scope")}>
                    {SCOPE.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label}>Budget</label>
                  <select data-testid="consult-budget" className={field} value={form.budget} onChange={set("budget")}>
                    {BUDGET.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={label}>Tell us about your project (optional)</label>
                <textarea data-testid="consult-message" rows={4} className={`${field} resize-none`} value={form.message} onChange={set("message")} placeholder="Rooms, timeline, inspiration…" />
              </div>

              <button
                data-testid="consult-submit"
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-3 bg-[#F8C21C] text-[#143D63] px-8 py-4 font-bold text-sm tracking-wide hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Request My Free Consultation"}
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
